import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import CustomSelect from '../../components/forms/CustomSelect';
import RequiredInput from '../../components/forms/InputHOC';
import useCorporations from '../../hooks/api-calls/useCorporations';
import useProducts from '../../hooks/api-calls/useProducts';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { InspectorCredentialsRow } from '../../type/entities/inspector-credentials-type';
import FormFooter from '../_layout/_footers/form-footer';
import { MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';

interface InspectorCredentialsFormProps {
    isLoading: boolean;
    submit: (values: Record<string, unknown>) => void;
    data?: InspectorCredentialsRow | undefined;
}

const schema = yup.object({
    productId: yup.string().required('El producto es obligatorio'),
    organisingCorporationId: yup.string().required('La entidad organizadora es obligatoria'),
    platformCif: yup.string().nullable(),
    platformBusinessName: yup.string().nullable(),
    platformUrl: yup.string().nullable(),
    inspectorUser: yup.string().nullable(),
    inspectorPassword: yup.string().nullable(),
});

const InspectorCredentialsForm: React.FC<InspectorCredentialsFormProps> = ({ isLoading, submit, data }) => {

    const mode = data ? 'Editar' : 'Crear';
    const navigate = useNavigate();
    const { fetchCorporations, getCorporationsList } = useCorporations({ active: true, corporation_function: 'organising' });
    const { fetchProducts, getProductsList } = useProducts({ active: true, product_type: '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1' });
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [optionsLoaded, setOptionsLoaded] = useState(false);

    useEffect(() => {
        const load = async () => {
            await fetchCorporations({});
            await fetchProducts({});
            setOptionsLoaded(true);
        };
        load();
    }, []);

    const formik = useFormik({
        initialValues: {
            inspectorCredentialsId: data?.id || '',
            productId: (data as any)?.product?.id || '',
            organisingCorporationId: data?.organisingCorporation?.id || '',
            platformCif: data?.platformCif || '',
            platformBusinessName: data?.platformBusinessName || '',
            platformUrl: data?.platformUrl || '',
            inspectorUser: data?.inspectorUser || '',
            inspectorPassword: data?.inspectorPassword || '',
        },
        validationSchema: schema,
        onSubmit: values => submit(values),
        enableReinitialize: true,
    });

    return (
        <>
            <Page container="fluid">
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                            <FormGroup
                                title="Credenciales de inspector"
                                description="Por producto (CursoBonificado) y entidad organizadora"
                                titleCols="@md:col-span-2"
                                childCols="@md:col-span-10 md:grid-cols-4"
                            >

                                {/* TODO: implementar selector de productos */}
                                <CustomSelect   
                                    id="productId"
                                    label="Producto (CursoBonificado)"
                                    required={true}
                                    isSearchable
                                    isClearable
                                    isDisabled={!!data}
                                    options={getProductsList()}
                                    value={formik.values.productId ? { value: formik.values.productId, label: getProductsList()?.find((g: any) => g.value === formik.values.productId)?.label } : null}
                                    onChange={(opt: { value: string } | null) => {
                                        formik.setFieldValue('productId', opt?.value ?? '');
                                    }}
                                    error={formik.touched.productId && formik.errors.productId}
                                    placeholder="producto"
                                />

                                <div className="@md:col-span-2">
                                    <CustomSelect
                                        id="organisingCorporationId"
                                        label="Entidad organizadora"
                                        required={true}
                                        isSearchable
                                        isClearable
                                        isDisabled={!!data || !optionsLoaded}
                                        options={getCorporationsList()}
                                        value={formik.values.organisingCorporationId ? { value: formik.values.organisingCorporationId, label: getCorporationsList()?.find((o: { value: string }) => o.value === formik.values.organisingCorporationId)?.label } : null}
                                        onChange={(opt: { value: string } | null) => {
                                            formik.setFieldValue('organisingCorporationId', opt?.value ?? '');
                                        }}
                                        error={formik.touched.organisingCorporationId && formik.errors.organisingCorporationId}
                                        placeholder="entidad organizadora"
                                    />
                                </div>
                                <RequiredInput
                                    required={false}
                                    id="platformCif"
                                    formik={formik}
                                    label="CIF plataforma"
                                    className="@md:col-span-2"
                                />
                                <RequiredInput
                                    required={false}
                                    id="platformBusinessName"
                                    formik={formik}
                                    label="Razón social plataforma"
                                    className="@md:col-span-2"
                                />
                                <RequiredInput
                                    required={false}
                                    id="platformUrl"
                                    formik={formik}
                                    label="URL acceso plataforma"
                                    className="@md:col-span-2"
                                />
                                <RequiredInput
                                    required={false}
                                    id="inspectorUser"
                                    formik={formik}
                                    label="Usuario inspector"
                                    className="@md:col-span-2"
                                />
                                <RequiredInput
                                    required={false}
                                    id="inspectorPassword"
                                    formik={formik}
                                    label="Contraseña inspector"
                                    className="@md:col-span-2"
                                    type={showPassword ? 'text' : 'password'}
                                    suffix={showPassword ? <MdOutlineVisibilityOff className="h-4 w-4" size={20} onClick={() => setShowPassword(!showPassword)}/> : <MdOutlineVisibility className="h-4 w-4" size={20} onClick={() => setShowPassword(!showPassword)}/>}
                                />
                            </FormGroup>
                        </div>
                    </form>
                </div>
            </Page>
            <FormFooter
                submitBtnText={mode + ' credenciales'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </>
    );
};

export default InspectorCredentialsForm;
